import { ThumbsUp, TrendingUp } from 'lucide-react';
import { OverViewIcon } from '../icon/overview-icon';
import { ArrowIcon } from '../icon/arrow-icon';
import { useTranslation } from 'react-i18next';

interface KeyStrength {
  strength: string;
  quote: string;
}
interface ImprovementArea {
  area: string;
  quote: string;
}

interface SummaryProps {
  summary: {
    overview: string;
    keyStrengths: KeyStrength[];
    areasForImprovement: ImprovementArea[];
    suggestedNextSteps: string[];
  };
}

export function SummaryContent({ summary }: SummaryProps) {
  const { t } = useTranslation();
  console.log('SummaryContent:', summary);
  return (
    <div className="space-y-8">
      {summary ? (
        <>
          <div>
            <div className="flex items-center gap-2 mb-4">
              <OverViewIcon className="h-5 w-5 text-[#f04e23]" />
              <h2 className="text-lg font-medium">
                {t('feedback.summary.overview', 'Overview')}
              </h2>
            </div>
            <p className="text-[#6b6866]">{summary.overview}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="border rounded-lg p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-6">
                <ThumbsUp className="h-5 w-5 text-[#069e71]" />
                <h3 className="text-lg font-medium">
                  {t('feedback.summary.key_strengths', 'Key strengths')}
                </h3>
              </div>
              {summary.keyStrengths.length > 0 ? (
                summary.keyStrengths.map((strength, index) => (
                  <ul className="space-y-6">
                    <li key={index} className="space-y-2 mb-4">
                      <div className="flex items-center gap-3 bg-[#f6f6f6] p-4 rounded-lg">
                        <span className="text-[#848484] text-2xl">•</span>
                        <p>{strength.strength}</p>
                      </div>
                      <p className="text-sm text-[#6b6866] px-6 italic border-l-2 border-[#e4e2e2] pl-4">
                        {strength.quote}
                      </p>
                    </li>
                  </ul>
                ))
              ) : (
                <div className="flex h-full items-center justify-center pb-9 text-center text-[#6b6866]">
                  {t('feedback.summary.no_strengths', 'No key strengths')}
                </div>
              )}
            </div>

            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="h-5 w-5 text-[#f6261a]" />
                <h3 className="text-lg font-medium">
                  {t(
                    'feedback.summary.areas_improvement',
                    'Areas for improvement',
                  )}
                </h3>
              </div>
              {summary.areasForImprovement.length > 0 ? (
                summary.areasForImprovement.map((area, index) => (
                  <ul className="space-y-6">
                    <li key={index} className="space-y-2 mb-4">
                      <div className="flex items-center gap-3 bg-[#f6f6f6] p-4 rounded-lg">
                        <span className="text-[#848484] text-2xl">•</span>
                        <p>{area.area}</p>
                      </div>
                      <p className="text-sm text-[#6b6866] px-6 italic border-l-2 border-[#e4e2e2] pl-4">
                        {area.quote}
                      </p>
                    </li>
                  </ul>
                ))
              ) : (
                <div className="flex h-full items-center justify-center pb-9 text-center text-[#6b6866]">
                  {t(
                    'feedback.summary.no_improvement_areas',
                    'No areas for improvement',
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-6">
              <ArrowIcon />
              <h3 className="text-lg font-medium">
                {t('feedback.summary.next_steps', 'Suggested next steps')}
              </h3>
            </div>
            <ul className="space-y-4 list-disc pl-5 text-[#6b6866] marker:text-[#6b6866]">
              {summary.suggestedNextSteps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          </div>
        </>
      ) : (
        <div className="flex justify-center items-center">
          <svg
            aria-hidden="true"
            className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
